import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { MODAL_TYPES } from '../../common/constants'
import i18n from '../../i18n'
import { setAccordionPage } from '../../store/accordionPagination'
import { hideModal } from '../../store/modal'
import { invalidateQueries, updateToast } from '../helpers'
import { createUpdateNote, deleteNoteById, getNotesById } from './functions'
import { Note, NoteDeleteParams, NoteDeleteResponse, NoteForm, NoteListRequest, NoteListResponse } from './types'

const notes = {
    GetNotes: {
        useQuery: (params: NoteListRequest, options?: UseQueryOptions<NoteListResponse, AxiosError>) =>
            useQuery({
                queryKey: ['notes', 'list', params],
                queryFn: () => getNotesById(params),
                staleTime: 1 * 60 * 1000,
                ...options,
            }),
    },
    CreateUpdateNote: {
        useMutation: (isEdit?: boolean, options?: UseMutationOptions<Note, AxiosError, NoteForm>) => {
            const dispatch = useDispatch()

            return useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.loading') as string)
                    return toastId
                },
                mutationFn: (data: NoteForm) => createUpdateNote(data, isEdit),
                onSuccess: (data, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    !isEdit && dispatch(setAccordionPage('notes', 1))
                    dispatch(hideModal({ modalType: MODAL_TYPES.NOTES_MODAL }))
                    invalidateQueries(['notes', 'list'])
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    DeleteNote: {
        useMutation: (options?: UseMutationOptions<NoteDeleteResponse, AxiosError, NoteDeleteParams>) =>
            useMutation({
                mutationFn: (params: NoteDeleteParams) => {
                    const { noteId, noteEntity } = params
                    return deleteNoteById(noteId, noteEntity)
                },
                onSuccess: () => invalidateQueries(['notes', 'list']),
                ...options,
            }),
    },
}

export default notes
